import {useEffect, useState} from 'react'
import {EditEmployeeModel, Employee} from '../../redux/types'

export interface EmployeeEditorValidation {
  maxPercentageValid: boolean
  maxCustomPercentageValid: boolean
  maxAmountValid: boolean
}

export interface EmployeeEditorChangeResult {
  employee: EditEmployeeModel
  valid: EmployeeEditorValidation
  isValid: boolean
}

export interface EmployeeEditor {
  employee: EditEmployeeModel
  valid: EmployeeEditorValidation
  isValid: boolean
  handleChange: (propName: keyof EditEmployeeModel, value: any) => EmployeeEditorChangeResult
  handlePayoutSettingsChange: (propName: keyof EditEmployeeModel['payoutSettings'], value: any) => EmployeeEditorChangeResult
  reset: () => EmployeeEditorChangeResult
}

const defaultValidationState: EmployeeEditorValidation = {
  maxPercentageValid: true,
  maxCustomPercentageValid: true,
  maxAmountValid: true,
}

const emptyEmployee: EditEmployeeModel = {
  paused: false,
  pauseReason: '',
  extendTerminationToUpcomingPayday: false,
  underForeclosure: false,
  nonEligible: false,
  payoutSettings: {
    maxAmountPerPayday: null,
    maxPercentagePerPayday: null,
    maxCustomPercentagePerPayday: null,
  },
  displayPayoutLimitationInfo: false,
  payoutLimitationReason: null,
  internalComment: null,
}

const evaluateValidation = (employee: EditEmployeeModel): {
  valid: EmployeeEditorValidation
  isValid: boolean
} => {
  let valid: EmployeeEditorValidation = {...defaultValidationState}
  let ok = true

  if (
    employee.payoutSettings?.maxAmountPerPayday != null &&
    employee.payoutSettings.maxAmountPerPayday < 0
  ) {
    valid = {...valid, maxAmountValid: false}
    ok = false
  }

  if (
    employee.payoutSettings?.maxPercentagePerPayday != null &&
    (employee.payoutSettings.maxPercentagePerPayday < 0 ||
      employee.payoutSettings.maxPercentagePerPayday > 100)
  ) {
    valid = {...valid, maxPercentageValid: false}
    ok = false
  }

  if (
    employee.payoutSettings?.maxCustomPercentagePerPayday != null &&
    (employee.payoutSettings.maxCustomPercentagePerPayday < 0 ||
      employee.payoutSettings.maxCustomPercentagePerPayday > 100)
  ) {
    valid = {...valid, maxCustomPercentageValid: false}
    ok = false
  }

  return {valid, isValid: ok}
}

const toEditEmployeeModel = (employee: Employee): EditEmployeeModel => ({
  extendTerminationToUpcomingPayday: employee.extendTerminationToUpcomingPayday,
  paused: employee.paused,
  pauseReason: employee.pauseReason,
  nonEligible: employee.nonEligible,
  underForeclosure: employee.underForeclosure,
  payoutSettings: {
    maxAmountPerPayday: employee.payoutSettings?.maxAmountPerPayday ?? null,
    maxPercentagePerPayday: employee.payoutSettings?.maxPercentagePerPayday ?? null,
    maxCustomPercentagePerPayday: employee.payoutSettings?.maxCustomPercentagePerPayday ?? null,
  },
  displayPayoutLimitationInfo: employee.displayPayoutLimitationInfo,
  payoutLimitationReason: employee.payoutLimitationReason,
  internalComment: employee.internalComment,
})

export const useEmployeeEditor = (initialValue?: Employee | null): EmployeeEditor => {
  const [employee, setEmployee] = useState<EditEmployeeModel>(emptyEmployee)
  const [initialSnapshot, setInitialSnapshot] = useState<EditEmployeeModel>(emptyEmployee)
  const [valid, setValid] = useState<EmployeeEditorValidation>(defaultValidationState)
  const [isValid, setIsValid] = useState<boolean>(true)

  useEffect(() => {
    if (!initialValue) {
      return // No employee loaded yet
    }

    const fromInitial = toEditEmployeeModel(initialValue)
    setEmployee(fromInitial)
    setInitialSnapshot(fromInitial)

    const {valid: v, isValid: ok} = evaluateValidation(fromInitial)
    setValid(v)
    setIsValid(ok)
  }, [initialValue])

  const applyAndValidate = (next: EditEmployeeModel): EmployeeEditorChangeResult => {
    setEmployee(next)
    const {valid: v, isValid: ok} = evaluateValidation(next)
    setValid(v)
    setIsValid(ok)
    return {employee: next, valid: v, isValid: ok}
  }

  const handleChange = (propName: keyof EditEmployeeModel, value: any) => {
    return applyAndValidate({
      ...employee,
      [propName]: value,
    })
  }

  const handlePayoutSettingsChange = (
    propName: keyof EditEmployeeModel['payoutSettings'],
    value: any
  ) => {
    return applyAndValidate({
      ...employee,
      payoutSettings: {
        ...employee.payoutSettings,
        [propName]: value === '' ? undefined : value,
      },
    })
  }

  const reset = () => {
    return applyAndValidate(initialSnapshot)
  }

  return {
    employee,
    valid,
    isValid,
    handleChange,
    handlePayoutSettingsChange,
    reset,
  }
}
